import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { ComplianceListItem } from '../../../../apis/settings/settingsComplianceListItem.interfaces';
import { ComplianceItem } from '../../../../apis/settings/settingsComplianceItem.interfaces';
import { useComplianceListHook } from '../hooks/useComplianceListHook';
import { useComplianceDetailsHook } from '../hooks/useComplianceDetailsHook';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';

type TComplianceManagementState = {
  errorMessage?: string;
  isLoading: boolean;
  isLoadingDetails: boolean;
  complianceList: ComplianceListItem[];
  totalCount: number;
  paginationParams: TPaginationParams;
  selectedComplianceId: string | null;
  selectedCompliance: ComplianceItem | null;
};

enum ACTION_TYPE {
  SET_COMPLIANCE_LIST = 'SET_COMPLIANCE_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCHING_DETAILS = 'FETCHING_DETAILS',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_PAGINATION_PARAMS = 'SET_PAGINATION_PARAMS',
  SET_SELECTED_COMPLIANCE_ID = 'SET_SELECTED_COMPLIANCE_ID',
  SET_SELECTED_COMPLIANCE = 'SET_SELECTED_COMPLIANCE',
}

type Action =
  | {
      type: ACTION_TYPE.SET_COMPLIANCE_LIST;
      payload: { complianceList: ComplianceListItem[]; totalCount: number };
    }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCHING_DETAILS; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_PAGINATION_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.SET_SELECTED_COMPLIANCE_ID; payload: string | null }
  | {
      type: ACTION_TYPE.SET_SELECTED_COMPLIANCE;
      payload: ComplianceItem | null;
    };

const initialState: TComplianceManagementState = {
  complianceList: [],
  errorMessage: undefined,
  isLoading: false,
  isLoadingDetails: false,
  paginationParams: {
    page: 1,
    limit: 10,
  },
  totalCount: 0,
  selectedComplianceId: null,
  selectedCompliance: null,
};

const ComplianceManagementContext = createContext<
  TComplianceManagementState | undefined
>(undefined);
const ComplianceManagementDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const complianceManagementReducer = (
  state: TComplianceManagementState,
  action: Action
): TComplianceManagementState => {
  switch (action.type) {
    case ACTION_TYPE.SET_COMPLIANCE_LIST:
      return {
        ...state,
        complianceList: action.payload.complianceList,
        totalCount: action.payload.totalCount,
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCHING_DETAILS:
      return { ...state, isLoadingDetails: action.payload };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_PAGINATION_PARAMS:
      return { ...state, paginationParams: action.payload };
    case ACTION_TYPE.SET_SELECTED_COMPLIANCE_ID:
      return {
        ...state,
        selectedComplianceId: action.payload,
        selectedCompliance: action.payload ? state.selectedCompliance : null,
      };
    case ACTION_TYPE.SET_SELECTED_COMPLIANCE:
      return { ...state, selectedCompliance: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const ComplianceManagementProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    complianceManagementReducer,
    initialState
  );

  const {
    data: complianceListResponse,
    isLoading: complianceListLoading,
    isError: complianceListError,
    error: complianceListErrorData,
  } = useComplianceListHook(state.paginationParams);

  const {
    data: complianceDetailsResponse,
    isLoading: complianceDetailsLoading,
    isError: complianceDetailsError,
    error: complianceDetailsErrorData,
  } = useComplianceDetailsHook(state.selectedComplianceId || '', {
    enabled: !!state.selectedComplianceId,
  });

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.FETCHING_DATA,
      payload: complianceListLoading,
    });
  }, [complianceListLoading]);

  useEffect(() => {
    if (complianceListResponse) {
      dispatch({
        type: ACTION_TYPE.SET_COMPLIANCE_LIST,
        payload: {
          complianceList: complianceListResponse.data,
          totalCount: complianceListResponse.total,
        },
      });
    }
  }, [complianceListResponse]);

  useEffect(() => {
    if (complianceListError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          complianceListErrorData?.response?.data.message ??
          ERROR_MESSAGE.GET_COMPLIANCE_LIST_FAILURE,
      });
    }
  }, [complianceListError, complianceListErrorData]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.FETCHING_DETAILS,
      payload: complianceDetailsLoading,
    });
  }, [complianceDetailsLoading]);

  useEffect(() => {
    if (complianceDetailsResponse && state.selectedComplianceId) {
      dispatch({
        type: ACTION_TYPE.SET_SELECTED_COMPLIANCE,
        payload: complianceDetailsResponse,
      });
    }
  }, [complianceDetailsResponse, state.selectedComplianceId]);

  useEffect(() => {
    if (complianceDetailsError && state.selectedComplianceId) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          complianceDetailsErrorData?.response?.data.message ??
          ERROR_MESSAGE.GET_COMPLIANCE_DETAILS_FAILURE,
      });
    }
  }, [
    complianceDetailsError,
    complianceDetailsErrorData,
    state.selectedComplianceId,
  ]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading: complianceListLoading,
    }),
    [state, complianceListLoading]
  );

  return (
    <ComplianceManagementContext.Provider value={value}>
      <ComplianceManagementDispatchContext.Provider value={dispatch}>
        {children}
      </ComplianceManagementDispatchContext.Provider>
    </ComplianceManagementContext.Provider>
  );
};

export const useComplianceManagementState = () => {
  const context = useContext(ComplianceManagementContext);
  if (context === undefined) {
    throw new Error(
      'useComplianceManagementState must be used within a ComplianceManagementProvider'
    );
  }
  return context;
};

export const useComplianceManagementActions = () => {
  const dispatch = useComplianceManagementDispatch();

  const updatePaginationParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_PAGINATION_PARAMS, payload: params });
  };

  const setSelectedComplianceId = (id: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_COMPLIANCE_ID, payload: id });
  };

  const setSelectedCompliance = (compliance: ComplianceItem | null) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_COMPLIANCE,
      payload: compliance,
    });
  };

  return {
    updatePaginationParams,
    setSelectedComplianceId,
    setSelectedCompliance,
  };
};

export const useComplianceManagementDispatch = () => {
  const context = useContext(ComplianceManagementDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useComplianceManagementDispatch must be used within a ComplianceManagementProvider'
    );
  }
  return context;
};
